import { useEffect, useState } from "react";

import { useSelector } from "react-redux";

import useCrmEnrichmentJobFilters from "app/pages/sales-intelligence/modules/crm-enrichment/impl/hubspot/hooks/useCrmEnrichmentJobFilters";
import type {
    CrmEnrichmentJobDTO,
    CrmEnrichmentJobObject,
    CrmEnrichmentJobRefreshPeriod,
    CrmEnrichmentJobType,
    CrmEnrichmentSource,
    EnrichmentJobMappingDTO,
    LeadScoreRefreshDataInterval,
} from "app/pages/sales-intelligence/modules/crm-enrichment/types/types";
import { getDefaultMappings } from "app/pages/sales-intelligence/modules/crm-enrichment/utils/defaultMappings";
import { createJobDTO, getDefaultCountryId } from "app/pages/sales-intelligence/modules/crm-enrichment/utils/job";
import {
    selectHsAccountsColumns,
    selectHsContactsColumns,
    selectHsSettings,
} from "app/pages/sales-intelligence/sub-modules/management/store/selectors/hubspot/exportSelectors";
import CountryService, { type ICountryObject } from "app/services/CountryService";

/** @deprecated use `useCrmEnrichmentJob` from `app/pages/sales-intelligence/modules/crm-enrichment/hooks/wizard/useCrmEnrichmentJob.ts` */
const useCrmEnrichmentJob = (initialJob?: CrmEnrichmentJobDTO) => {
    const hsSettings = useSelector(selectHsSettings);

    const [jobName, setJobName] = useState("");
    const [selectedJobType, setSelectedJobType] = useState<CrmEnrichmentJobType | "">("");
    const [selectedJobObject, setSelectedJobObject] = useState<CrmEnrichmentJobObject>("company");
    const [selectedJobRefreshPeriod, setSelectedJobRefreshPeriod] =
        useState<CrmEnrichmentJobRefreshPeriod>("asSoonAsPossible");
    const [selectedEnrichmentSource, setSelectedEnrichmentSource] = useState<CrmEnrichmentSource>("company");
    const [selectedLeadScoreRefreshDataInterval, setSelectedLeadScoreRefreshDataInterval] =
        useState<LeadScoreRefreshDataInterval | "">("weekly");

    const [selectedCountry, setSelectedCountry] = useState<ICountryObject>(() => {
        const defaultCountryId = getDefaultCountryId();

        return CountryService.getCountryById(defaultCountryId);
    });

    const [mappings, setMappings] = useState<readonly EnrichmentJobMappingDTO[]>(() =>
        getDefaultMappings("hubspot", "company", hsSettings.accounts),
    );

    const accountsColumns = useSelector(selectHsAccountsColumns);
    const contactColumns = useSelector(selectHsContactsColumns);
    const columns = selectedJobObject === "company" ? accountsColumns : contactColumns;
    const initialColumns = initialJob?.enrichmentObject === "company" ? accountsColumns : contactColumns;

    const getHsExportSettings = (object: CrmEnrichmentJobObject) =>
        object === "company" ? hsSettings.accounts : hsSettings.contacts;

    useEffect(() => {
        if (initialJob) {
            setJobName(initialJob.name);
            setSelectedJobType(initialJob.type);
            setSelectedJobObject(initialJob.enrichmentObject);
            setSelectedJobRefreshPeriod(initialJob.refreshDataInterval);
            setSelectedEnrichmentSource(initialJob.enrichmentSource);
            setSelectedLeadScoreRefreshDataInterval(initialJob.leadScoreRefreshDataInterval ?? "weekly");
            setSelectedCountry(CountryService.getCountryById(initialJob.country));
            setMappings(
                initialJob.mappings ??
                    getDefaultMappings("hubspot", initialJob.enrichmentObject, getHsExportSettings(initialJob.enrichmentObject)),
            );
        }
    }, [initialJob]);

    const handleAddMapping = (mapping: EnrichmentJobMappingDTO): void => {
        setMappings((prev) => [...prev, mapping]);
    };

    const handleRemoveMapping = (index: number): void => {
        setMappings((prev) => prev.filter((_, i) => i !== index));
    };

    const handleUpdateMapping = (index: number, changes: Partial<EnrichmentJobMappingDTO>): void => {
        setMappings((prev) => prev.map((m, i) => (i === index ? { ...m, ...changes } : m)));
    };

    const resetMappingsForObject = (object: CrmEnrichmentJobObject): void => {
        setMappings(getDefaultMappings("hubspot", object, getHsExportSettings(object)));
    };

    const {
        isApplyFiltersSelected,
        onApplyFiltersChange,
        filters,
        onAddFilter,
        onDeleteFilter,
        onFilterChange,
        onCrmFieldFilterChange,
        onResetFilters,
        setFiltersFromInitial: setFiltersFromInitialWithParams,
        crmFieldOptions,
    } = useCrmEnrichmentJobFilters({
        columns,
        initialColumns,
        initialFilters: initialJob?.filters,
    });

    const setFiltersFromInitial = () => {
        setFiltersFromInitialWithParams(initialJob?.filters, initialColumns);
    };

    const getJobDTO = () =>
        createJobDTO({
            jobName,
            jobType: selectedJobType,
            jobObject: selectedJobObject,
            countryId: selectedCountry.id,
            refreshPeriod: selectedJobRefreshPeriod,
            leadScoreRefreshDataInterval: selectedLeadScoreRefreshDataInterval,
            enrichmentSource: selectedEnrichmentSource,
            isApplyFiltersSelected,
            filters,
            columns,
            initialJob,
            mappings,
        });

    return {
        createJobDTO: getJobDTO,
        jobName,
        setJobName,
        selectedJobType,
        setSelectedJobType,
        selectedJobObject,
        setSelectedJobObject,
        selectedJobRefreshPeriod,
        setSelectedJobRefreshPeriod,
        selectedEnrichmentSource,
        setSelectedEnrichmentSource,
        selectedLeadScoreRefreshDataInterval,
        setSelectedLeadScoreRefreshDataInterval,
        selectedCountry,
        setSelectedCountry,
        onApplyFiltersChange,
        filters,
        onAddFilter,
        onDeleteFilter,
        onFilterChange,
        onCrmFieldFilterChange,
        onResetFilters,
        setFiltersFromInitial,
        crmFieldOptions,
        isApplyFiltersSelected,
        mappings,
        setMappings,
        addMapping: handleAddMapping,
        removeMapping: handleRemoveMapping,
        updateMapping: handleUpdateMapping,
        resetMappingsForObject,
    };
};

export default useCrmEnrichmentJob;
